import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import MainLayout from '../components/layout/MainLayout';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('email');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Configurações de Email
  const [emailSettings, setEmailSettings] = useState({
    smtp_server: '',
    smtp_port: '',
    smtp_username: '',
    smtp_password: '',
    sender_email: '',
    report_recipients: '',
    enable_email_reports: false
  });

  // Configurações de Moeda
  const [currencySettings, setCurrencySettings] = useState({
    default_currency: 'USD',
    api_key: '',
    enable_currency_conversion: true,
    available_currencies: ['USD', 'EUR', 'GBP', 'BRL'],
    exchange_rates: {
      USD: 1,
      EUR: 0.93,
      GBP: 0.79,
      BRL: 5.20
    },
    update_frequency: 'daily'
  });

  // Carregar configurações do backend
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      // Buscar configurações de email
      const emailResponse = await axios.get('/api/settings/email');
      if (emailResponse.data) {
        setEmailSettings(emailResponse.data);
      }

      // Buscar configurações de moeda
      try {
        const currencyResponse = await axios.get('/api/settings/currency');
        if (currencyResponse.data) {
          setCurrencySettings(currencyResponse.data);
        }
      } catch (error) {
        console.log('Configurações de moeda não encontradas, usando padrões');
        // Manter os valores padrão se não houver configurações salvas
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  // Salvar configurações de email
  const saveEmailSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post('/api/settings/email', emailSettings);
      toast.success('Configurações de email salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações de email:', error);
      toast.error('Erro ao salvar configurações de email');
    } finally {
      setSaving(false);
    }
  };

  // Salvar configurações de moeda
  const saveCurrencySettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post('/api/settings/currency', currencySettings);
      toast.success('Configurações de moeda salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações de moeda:', error);
      toast.error('Erro ao salvar configurações de moeda');
    } finally {
      setSaving(false);
    }
  };

  // Atualizar valor nas configurações de email
  const handleEmailChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEmailSettings(prevSettings => ({
      ...prevSettings,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Atualizar valor nas configurações de moeda
  const handleCurrencyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrencySettings(prevSettings => ({
      ...prevSettings,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Adicionar/Remover moeda disponível
  const toggleCurrency = (currency) => {
    setCurrencySettings(prevSettings => {
      const currencies = [...prevSettings.available_currencies];
      
      if (currencies.includes(currency)) {
        // Não permitir remover a moeda padrão
        if (currency === prevSettings.default_currency) {
          toast.warning('Não é possível remover a moeda padrão');
          return prevSettings;
        }
        return {
          ...prevSettings,
          available_currencies: currencies.filter(c => c !== currency)
        };
      } else {
        return {
          ...prevSettings,
          available_currencies: [...currencies, currency]
        };
      }
    });
  };

  return (
    <MainLayout>
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="card my-4">
              <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                <div className="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3">
                  <h6 className="text-white text-capitalize ps-3">Configurações do Sistema</h6>
                </div>
              </div>
              
              <div className="card-body px-0 pb-2">
                <div className="px-4">
                  {/* Abas de navegação */}
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button 
                        className={`nav-link ${activeTab === 'email' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('email')}
                      >
                        Configurações de Email
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button 
                        className={`nav-link ${activeTab === 'currency' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('currency')}
                      >
                        Configurações de Moeda
                      </button>
                    </li>
                  </ul>
                
                  {/* Conteúdo das abas */}
                  <div className="tab-content mt-4">
                    {/* Aba de Configurações de Email */}
                    {activeTab === 'email' && (
                      <div className="tab-pane fade show active">
                        <h5 className="card-title mb-4">Configurações do Servidor de Email</h5>
                        
                        <form onSubmit={saveEmailSettings}>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="input-group input-group-outline mb-3">
                                <label className="form-label">Servidor SMTP</label>
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  name="smtp_server" 
                                  value={emailSettings.smtp_server} 
                                  onChange={handleEmailChange} 
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="input-group input-group-outline mb-3">
                                <label className="form-label">Porta SMTP</label>
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  name="smtp_port" 
                                  value={emailSettings.smtp_port} 
                                  onChange={handleEmailChange} 
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="row">
                            <div className="col-md-6">
                              <div className="input-group input-group-outline mb-3">
                                <label className="form-label">Nome de Usuário SMTP</label>
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  name="smtp_username" 
                                  value={emailSettings.smtp_username} 
                                  onChange={handleEmailChange} 
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="input-group input-group-outline mb-3">
                                <label className="form-label">Senha SMTP</label>
                                <input 
                                  type="password" 
                                  className="form-control" 
                                  name="smtp_password" 
                                  value={emailSettings.smtp_password} 
                                  onChange={handleEmailChange} 
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="row">
                            <div className="col-md-6">
                              <div className="input-group input-group-outline mb-3">
                                <label className="form-label">Email do Remetente</label>
                                <input 
                                  type="email" 
                                  className="form-control" 
                                  name="sender_email" 
                                  value={emailSettings.sender_email} 
                                  onChange={handleEmailChange} 
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="input-group input-group-outline mb-3">
                                <label className="form-label">Destinatários (separados por vírgula)</label>
                                <input 
                                  type="text" 
                                  className="form-control" 
                                  name="report_recipients" 
                                  value={emailSettings.report_recipients} 
                                  onChange={handleEmailChange} 
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="form-check mb-3">
                            <input 
                              className="form-check-input" 
                              type="checkbox" 
                              id="enableEmailReports" 
                              name="enable_email_reports" 
                              checked={emailSettings.enable_email_reports} 
                              onChange={handleEmailChange} 
                            />
                            <label className="form-check-label" htmlFor="enableEmailReports">
                              Ativar envio automático de relatórios por email
                            </label>
                          </div>
                          
                          <button 
                            type="submit" 
                            className="btn btn-primary" 
                            disabled={saving}
                          >
                            {saving ? 'Salvando...' : 'Salvar Configurações'}
                          </button>
                        </form>
                      </div>
                    )}
                    
                    {/* Aba de Configurações de Moeda */}
                    {activeTab === 'currency' && (
                      <div className="tab-pane fade show active">
                        <h5 className="card-title mb-4">Configurações de Moeda</h5>
                        
                        <form onSubmit={saveCurrencySettings}>
                          <div className="row mb-4">
                            <div className="col-md-6">
                              <label className="form-label">Moeda Padrão</label>
                              <select 
                                className="form-select" 
                                name="default_currency" 
                                value={currencySettings.default_currency} 
                                onChange={handleCurrencyChange}
                              >
                                <option value="USD">Dólar Americano (USD)</option>
                                <option value="EUR">Euro (EUR)</option>
                                <option value="BRL">Real Brasileiro (BRL)</option>
                                <option value="GBP">Libra Esterlina (GBP)</option>
                              </select>
                            </div>
                            
                            <div className="col-md-6">
                              <label className="form-label">Frequência de Atualização das Taxas</label>
                              <select 
                                className="form-select" 
                                name="update_frequency" 
                                value={currencySettings.update_frequency} 
                                onChange={handleCurrencyChange}
                              >
                                <option value="daily">Diariamente</option>
                                <option value="hourly">A cada hora</option>
                                <option value="weekly">Semanalmente</option>
                                <option value="manual">Manual</option>
                              </select>
                            </div>
                          </div>
                          
                          <div className="row mb-4">
                            <div className="col-md-12">
                              <label className="form-label">Chave da API de Conversão (opcional)</label>
                              <input 
                                type="text" 
                                className="form-control" 
                                name="api_key" 
                                value={currencySettings.api_key} 
                                onChange={handleCurrencyChange} 
                                placeholder="Chave para API externa de taxas de câmbio (ex: exchangeratesapi.io)"
                              />
                              <small className="text-muted">
                                Se não fornecida, o sistema usará taxas estáticas predefinidas.
                              </small>
                            </div>
                          </div>
                          
                          <div className="form-check mb-4">
                            <input 
                              className="form-check-input" 
                              type="checkbox" 
                              id="enableCurrencyConversion" 
                              name="enable_currency_conversion" 
                              checked={currencySettings.enable_currency_conversion} 
                              onChange={handleCurrencyChange} 
                            />
                            <label className="form-check-label" htmlFor="enableCurrencyConversion">
                              Ativar conversão de moedas nos relatórios e simulações
                            </label>
                          </div>
                          
                          <div className="mb-4">
                            <label className="form-label">Moedas Disponíveis</label>
                            <div className="d-flex flex-wrap gap-2">
                              {['USD', 'EUR', 'GBP', 'BRL'].map(currency => (
                                <div key={currency} className="form-check">
                                  <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    id={`currency-${currency}`} 
                                    checked={currencySettings.available_currencies.includes(currency)} 
                                    onChange={() => toggleCurrency(currency)} 
                                  />
                                  <label className="form-check-label" htmlFor={`currency-${currency}`}>
                                    {currency === 'USD' ? 'Dólar (USD)' :
                                     currency === 'EUR' ? 'Euro (EUR)' :
                                     currency === 'GBP' ? 'Libra (GBP)' :
                                     'Real (BRL)'}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div className="mb-4">
                            <h6>Taxas de Câmbio Atuais</h6>
                            <div className="table-responsive">
                              <table className="table">
                                <thead>
                                  <tr>
                                    <th>Moeda</th>
                                    <th>Taxa em relação ao USD</th>
                                    <th>Atualizado em</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(currencySettings.exchange_rates).map(([currency, rate]) => (
                                    <tr key={currency}>
                                      <td>{currency}</td>
                                      <td>{rate.toFixed(4)}</td>
                                      <td>Hoje</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <button 
                              type="button" 
                              className="btn btn-sm btn-outline-primary mt-2"
                              onClick={() => {
                                // Aqui adicionaremos a lógica para atualizar taxas via API
                                toast.info('Funcionalidade em desenvolvimento');
                              }}
                            >
                              Atualizar Taxas
                            </button>
                          </div>
                          
                          <button 
                            type="submit" 
                            className="btn btn-primary" 
                            disabled={saving}
                          >
                            {saving ? 'Salvando...' : 'Salvar Configurações'}
                          </button>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Settings; 